import { MousePointer2, Type, Timer, Table2 } from "lucide-react";

/**
 * Mini animação CSS pura mostrando os 4 passos a acontecer num "browser" simulado.
 * Cicla a cada ~10s.
 */
export default function RecorderDemo() {
  return (
    <div className="rounded-xl border border-border bg-gradient-to-br from-primary/5 to-transparent p-3 overflow-hidden">
      <div className="text-xs font-semibold mb-2 text-foreground flex items-center gap-1.5">
        <span className="h-1.5 w-1.5 rounded-full bg-red-500 animate-pulse"/>
        Demo: como uma recipe é gravada
      </div>

      <div className="relative aspect-[16/8] rounded-lg border border-border bg-card overflow-hidden shadow-inner">
        {/* Browser chrome */}
        <div className="absolute top-0 inset-x-0 h-6 bg-muted/70 border-b border-border flex items-center px-2 gap-1.5">
          <span className="h-2 w-2 rounded-full bg-red-400"/>
          <span className="h-2 w-2 rounded-full bg-amber-400"/>
          <span className="h-2 w-2 rounded-full bg-emerald-400"/>
          <div className="ml-2 flex-1 h-3.5 rounded bg-card border border-border flex items-center px-1.5">
            <span className="text-[8px] text-muted-foreground font-mono demo-url"></span>
          </div>
        </div>

        {/* Stage */}
        <div className="absolute inset-0 top-6">
          {/* Step 1: typing URL */}
          <div className="demo-step demo-step-1 absolute inset-0 grid place-items-center text-[10px] text-muted-foreground">
            <span>1. Cola URL → <span className="text-primary font-semibold">Iniciar sessão</span></span>
          </div>

          {/* Step 2: page renders + click */}
          <div className="demo-step demo-step-2 absolute inset-0 p-2">
            <div className="text-[8px] font-semibold mb-1">Login</div>
            <div className="space-y-1">
              <div className="h-2.5 rounded bg-muted w-3/5"/>
              <div className="h-2.5 rounded bg-muted w-2/5"/>
              <div className="demo-btn h-3 rounded bg-primary/80 w-1/4 mt-1.5 flex items-center justify-center text-[7px] text-primary-foreground">Entrar</div>
            </div>
            <div className="demo-cursor"><MousePointer2 className="h-3 w-3 text-[hsl(var(--step-click))]"/></div>
          </div>

          {/* Step 3: filling input */}
          <div className="demo-step demo-step-3 absolute inset-0 p-2">
            <div className="text-[8px] font-semibold mb-1">Filtros</div>
            <div className="grid grid-cols-2 gap-1.5">
              <div className="h-3.5 rounded border border-[hsl(var(--step-fill))]/40 bg-card px-1 flex items-center text-[7px] font-mono">
                <span className="demo-typed">2026-05-13</span>
              </div>
              <div className="h-3.5 rounded border border-border bg-card"/>
            </div>
            <div className="demo-cursor demo-cursor-fill"><Type className="h-3 w-3 text-[hsl(var(--step-fill))]"/></div>
          </div>

          {/* Step 4: wait spinner */}
          <div className="demo-step demo-step-4 absolute inset-0 grid place-items-center">
            <div className="flex items-center gap-1.5 text-[9px] text-muted-foreground">
              <Timer className="h-3 w-3 text-[hsl(var(--step-wait))] animate-spin"/>
              A carregar…
            </div>
          </div>

          {/* Step 5: extract table */}
          <div className="demo-step demo-step-5 absolute inset-0 p-2">
            <div className="text-[8px] font-semibold mb-1 flex items-center gap-1">
              <Table2 className="h-2.5 w-2.5 text-[hsl(var(--step-extract))]"/>
              Tabela
            </div>
            <div className="rounded border border-[hsl(var(--step-extract))]/50 bg-[hsl(var(--step-extract))]/5 p-1 space-y-0.5">
              <div className="flex gap-1">
                <div className="h-2 rounded bg-muted-foreground/40 flex-1"/>
                <div className="h-2 rounded bg-muted-foreground/40 flex-1"/>
                <div className="h-2 rounded bg-muted-foreground/40 flex-1"/>
              </div>
              <div className="flex gap-1"><div className="h-2 rounded bg-muted flex-1"/><div className="h-2 rounded bg-muted flex-1"/><div className="h-2 rounded bg-muted flex-1"/></div>
              <div className="flex gap-1"><div className="h-2 rounded bg-muted flex-1"/><div className="h-2 rounded bg-muted flex-1"/><div className="h-2 rounded bg-muted flex-1"/></div>
              <div className="flex gap-1"><div className="h-2 rounded bg-muted flex-1"/><div className="h-2 rounded bg-muted flex-1"/><div className="h-2 rounded bg-muted flex-1"/></div>
            </div>
          </div>
        </div>

        {/* Badge ciclando no canto */}
        <div className="absolute bottom-1.5 right-1.5 text-[8px] uppercase tracking-wider px-1.5 py-0.5 rounded bg-card/90 border border-border font-semibold demo-badge"></div>
      </div>

      <style>{`
        @keyframes demoStep1 { 0%, 18% { opacity: 1; } 22%, 100% { opacity: 0; } }
        @keyframes demoStep2 { 0%, 20% { opacity: 0; } 24%, 38% { opacity: 1; } 42%, 100% { opacity: 0; } }
        @keyframes demoStep3 { 0%, 40% { opacity: 0; } 44%, 58% { opacity: 1; } 62%, 100% { opacity: 0; } }
        @keyframes demoStep4 { 0%, 60% { opacity: 0; } 64%, 72% { opacity: 1; } 76%, 100% { opacity: 0; } }
        @keyframes demoStep5 { 0%, 74% { opacity: 0; } 78%, 98% { opacity: 1; } 100% { opacity: 0; } }
        .demo-step { opacity: 0; }
        .demo-step-1 { animation: demoStep1 10s linear infinite; }
        .demo-step-2 { animation: demoStep2 10s linear infinite; }
        .demo-step-3 { animation: demoStep3 10s linear infinite; }
        .demo-step-4 { animation: demoStep4 10s linear infinite; }
        .demo-step-5 { animation: demoStep5 10s linear infinite; }

        @keyframes demoUrl {
          0% { content: 'https://'; }
          5% { content: 'https://crm'; }
          10% { content: 'https://crm.exemp'; }
          15%, 100% { content: 'https://crm.exemplo.pt/login'; }
        }
        .demo-url::after {
          content: 'https://crm.exemplo.pt/login';
          animation: demoUrl 10s linear infinite;
        }

        @keyframes demoCursor {
          0%, 22% { transform: translate(20%, 20%); opacity: 0; }
          26% { transform: translate(60%, 130%); opacity: 1; }
          32% { transform: translate(45%, 165%); opacity: 1; }
          38% { transform: translate(45%, 165%) scale(0.85); opacity: 1; }
          42%, 100% { opacity: 0; }
        }
        .demo-cursor {
          position: absolute;
          top: 0;
          left: 0;
          animation: demoCursor 10s linear infinite;
        }

        @keyframes demoCursorFill {
          0%, 42% { opacity: 0; transform: translate(10%, 40%); }
          46% { opacity: 1; transform: translate(20%, 60%); }
          52% { opacity: 1; transform: translate(20%, 60%); }
          60%, 100% { opacity: 0; }
        }
        .demo-cursor-fill {
          animation: demoCursorFill 10s linear infinite;
        }

        @keyframes demoTyped {
          0%, 46% { content: ''; }
          48% { content: '20'; }
          50% { content: '2026'; }
          52% { content: '2026-05'; }
          54%, 100% { content: '2026-05-13'; }
        }
        .demo-typed::after {
          content: '2026-05-13';
          animation: demoTyped 10s linear infinite;
        }

        @keyframes demoBadge {
          0%, 18% { content: '1 · URL'; background: rgba(56, 189, 248, 0.15); }
          24%, 38% { content: '2 · Clicar'; }
          44%, 58% { content: '3 · Preencher'; }
          64%, 72% { content: '4 · Esperar'; }
          78%, 100% { content: '5 · Extrair'; }
        }
        .demo-badge::after {
          content: '1 · URL';
          animation: demoBadge 10s linear infinite;
        }
      `}</style>
    </div>
  );
}
